import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  Body,
  Hero,
  HeroEyebrow,
  HeroTitle,
  HeroSub,
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
  PortcoEmbeddedDark,
} from '../portco/PortcoTheme';

/**
 * Risk Detail — per-portfolio drilldown (Aladdin-tier).
 *
 * Four VaR methods side-by-side (parametric / historical 1y-3y-5y / Monte Carlo /
 * copula-aware), a factor-shock simulator (rerun MC with custom factor loadings),
 * and a copula correlation matrix across the chosen factor set.
 *
 * Endpoints:
 *   GET  /api/risk/portfolio/:id/var                          (existing parametric)
 *   GET  /api/risk/var/historical/portfolio/:id/strip
 *   POST /api/risk/var/montecarlo/portfolio/:id
 *   GET  /api/risk/copula/correlation?indexCodes=...
 *   GET  /api/dimensions/portfolios
 */

const FACTOR_PRESETS: Array<{ label: string; factors: { indexCode: string; beta: number }[] }> = [
  { label: 'Equity 60 / Credit 40', factors: [{ indexCode: 'SPY', beta: 0.6 }, { indexCode: 'HYG', beta: 0.4 }] },
  { label: 'Equity 40 / Credit 60', factors: [{ indexCode: 'SPY', beta: 0.4 }, { indexCode: 'HYG', beta: 0.6 }] },
  { label: 'Rates-led', factors: [{ indexCode: 'TLT', beta: 0.7 }, { indexCode: 'HYG', beta: 0.3 }] },
  { label: 'Small-cap + HY', factors: [{ indexCode: 'IWM', beta: 0.5 }, { indexCode: 'HYG', beta: 0.5 }] },
];

const CONFIDENCE_PRESETS = [0.95, 0.99] as const;

interface ParametricVaR {
  portfolioId: number;
  portfolioName?: string;
  asOfDate: string;
  totalMarketValue?: number;
  duration?: number;
  var?: any;
}

interface HistoricalVaRWindow {
  method: string;
  varDollars: number;
  varReturn?: number;
  expectedShortfallDollars?: number;
  expectedShortfallReturn?: number;
  observations?: number;
  windowDays?: number;
  marketValue?: number;
  beta?: number;
  indexCode?: string;
  error?: string;
}

interface HistoricalStrip {
  oneYear: HistoricalVaRWindow;
  threeYear: HistoricalVaRWindow;
  fiveYear: HistoricalVaRWindow;
}

interface MCFactorOut {
  indexCode: string;
  beta: number;
  muDaily: number;
  sigmaDaily: number;
}

interface MCResult {
  method: string;
  portfolioId: number;
  marketValue: number;
  paths: number;
  factors: MCFactorOut[];
  confidence: number;
  holdingDays: number;
  varDollars: number;
  expectedShortfallDollars: number;
  seed?: number;
  error?: string;
}

interface CopulaResult {
  indices?: string[];
  observations?: number;
  startDate?: string;
  endDate?: string;
  pearson?: Record<string, Record<string, number>>;
  spearmanCopulaProxy?: Record<string, Record<string, number>>;
  lowerTailCoExceedance5pct?: Record<string, Record<string, number>>;
  error?: string;
}

interface PortfolioLite {
  id: number;
  name: string;
  portfolioCode?: string;
}

const fmtMoney = (v: number | null | undefined): string => {
  if (v == null || isNaN(v as any)) return '—';
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtPct = (v: number | null | undefined, digits = 2): string => {
  if (v == null || isNaN(v as any)) return '—';
  return `${(Number(v) * 100).toFixed(digits)}%`;
};

const fmtNum = (v: number | null | undefined, digits = 4): string => {
  if (v == null || isNaN(v as any)) return '—';
  return Number(v).toFixed(digits);
};

const FactorRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 28px;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
  & input {
    width: 100%;
    border: 1px solid ${portcoTokens.hairline};
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 12px;
    font-family: ${portcoTokens.fontMono};
    /* #86: raw inputs defaulted to white on the now-dark surface. */
    background: rgba(15, 23, 42, 0.55);
    color: ${portcoTokens.inkPrimary};
  }
  & input::placeholder {
    color: ${portcoTokens.inkSubtle};
  }
  & input:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const FactorPresetRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const ChipBtn = styled.button<{ $active?: boolean }>`
  /* #86: the token active state (inkPrimary bg / surfaceBody text) inverts to a
     white pill with invisible text under the dark embedded palette. Use an
     explicit indigo-filled active + translucent inactive to match Analytics. */
  border: 1px solid
    ${(p) => (p.$active ? 'transparent' : 'rgba(255, 255, 255, 0.14)')};
  background: ${(p) => (p.$active ? '#6366f1' : 'rgba(255, 255, 255, 0.04)')};
  color: ${(p) => (p.$active ? '#ffffff' : portcoTokens.inkPrimary)};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
`;

const RunButton = styled.button`
  /* #86: inkPrimary bg / surfaceBody text inverts to a white pill with an
     invisible label under the dark palette — indigo-filled primary CTA. */
  background: #6366f1;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  margin-top: 8px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SmallLink = styled.button`
  background: transparent;
  border: none;
  color: ${portcoTokens.accent};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  margin-bottom: 12px;
  &:hover {
    text-decoration: underline;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: 16px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const VarCmpGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const VarCard = styled.div<{ $tone?: 'parametric' | 'historical' | 'mc' | 'copula' }>`
  border: 1px solid ${portcoTokens.hairline};
  border-radius: 10px;
  padding: 14px 16px 12px;
  background: ${portcoTokens.surfaceTile};
  border-top: 3px solid
    ${(p) =>
      p.$tone === 'parametric'
        ? '#3b82f6'
        : p.$tone === 'historical'
        ? '#14b8a6'
        : p.$tone === 'mc'
        ? '#a855f7'
        : '#f97316'};
`;

const VarLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 4px;
`;

const VarValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${portcoTokens.inkPrimary};
  font-variant-numeric: tabular-nums;
  margin-bottom: 6px;
`;

const VarSub = styled.div`
  font-size: 11px;
  color: ${portcoTokens.inkSubtle};
  line-height: 1.5;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-family: ${portcoTokens.fontMono};
  & th,
  & td {
    padding: 8px 10px;
    text-align: center;
    border-bottom: 1px solid ${portcoTokens.divider};
    border-right: 1px solid ${portcoTokens.divider};
    font-variant-numeric: tabular-nums;
  }
  & thead th {
    background: ${portcoTokens.surfaceMuted};
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${portcoTokens.inkPrimary};
  }
  & tbody td:first-child {
    font-weight: 600;
    background: ${portcoTokens.surfaceMuted};
    text-align: left;
  }
`;

const corrCellBg = (v: number | undefined): string => {
  if (v == null || isNaN(v as any)) return 'transparent';
  // diverging blue (negative) → white (zero) → red (positive)
  const clamped = Math.max(-1, Math.min(1, v));
  const intensity = Math.abs(clamped);
  if (clamped >= 0) return `rgba(239, 68, 68, ${intensity * 0.35})`;
  return `rgba(59, 130, 246, ${intensity * 0.35})`;
};

const tailCellBg = (v: number | undefined): string => {
  if (v == null || isNaN(v as any)) return 'transparent';
  const clamped = Math.max(0, Math.min(1, v));
  return `rgba(168, 85, 247, ${clamped * 0.5})`;
};

const RiskDetailScreen: React.FC = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const navigate = useNavigate();
  const pid = portfolioId ? parseInt(portfolioId, 10) : NaN;

  const [portfolio, setPortfolio] = useState<PortfolioLite | null>(null);
  const [parametric, setParametric] = useState<ParametricVaR | null>(null);
  const [histStrip, setHistStrip] = useState<HistoricalStrip | null>(null);
  const [mc, setMc] = useState<MCResult | null>(null);
  const [copula, setCopula] = useState<CopulaResult | null>(null);

  const [confidence, setConfidence] = useState<number>(0.99);
  const [factors, setFactors] = useState(FACTOR_PRESETS[0].factors.map((f) => ({ ...f })));
  const [activePreset, setActivePreset] = useState<number>(0);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const indexCodes = useMemo(
    () => factors.map((f) => f.indexCode).filter((c) => c && c.trim().length > 0),
    [factors],
  );

  // Initial load (portfolio meta + parametric + historical + MC + copula)
  useEffect(() => {
    if (!pid || isNaN(pid)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [portfoliosResp, parametricResp, histResp, copulaResp] = await Promise.all([
          axios.get('/api/dimensions/portfolios'),
          axios
            .post<ParametricVaR>(`/api/risk/portfolio/${pid}/refresh`)
            .catch(() => axios.get<ParametricVaR>(`/api/risk/portfolio/${pid}/var`))
            .catch(() => null),
          axios
            .get<HistoricalStrip>(
              `/api/risk/var/historical/portfolio/${pid}/strip?indexCode=SPY&confidence=${confidence}`,
            )
            .catch(() => null),
          axios
            .get<CopulaResult>(
              `/api/risk/copula/correlation?indexCodes=${indexCodes.join(',')}&historyDays=504`,
            )
            .catch(() => null),
        ]);
        if (cancelled) return;

        const list = Array.isArray(portfoliosResp.data)
          ? portfoliosResp.data
          : portfoliosResp.data?.content || [];
        const p = list.find((x: any) => x.id === pid);
        if (p) setPortfolio({ id: p.id, name: p.name, portfolioCode: p.portfolioCode });

        if (parametricResp) setParametric(parametricResp.data);
        if (histResp) setHistStrip(histResp.data);
        if (copulaResp) setCopula(copulaResp.data);

        // First MC run with the default preset
        const mcResp = await axios.post<MCResult>(
          `/api/risk/var/montecarlo/portfolio/${pid}?paths=10000&historyDays=504&confidence=${confidence}`,
          { factors },
        );
        if (cancelled) return;
        setMc(mcResp.data);
      } catch (e: any) {
        if (!cancelled)
          setError(e?.response?.data?.message || e?.message || 'Failed to load Risk Detail.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // Reload historical strip when confidence changes
  useEffect(() => {
    if (!pid || isNaN(pid)) return;
    let cancelled = false;
    (async () => {
      try {
        const histResp = await axios.get<HistoricalStrip>(
          `/api/risk/var/historical/portfolio/${pid}/strip?indexCode=SPY&confidence=${confidence}`,
        );
        if (!cancelled) setHistStrip(histResp.data);
      } catch (e: any) {
        // non-fatal
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pid, confidence]);

  const runScenario = async () => {
    if (!pid || isNaN(pid)) return;
    setRunning(true);
    setError(null);
    try {
      const [mcResp, copulaResp] = await Promise.all([
        axios.post<MCResult>(
          `/api/risk/var/montecarlo/portfolio/${pid}?paths=10000&historyDays=504&confidence=${confidence}`,
          { factors },
        ),
        indexCodes.length >= 2
          ? axios.get<CopulaResult>(
              `/api/risk/copula/correlation?indexCodes=${indexCodes.join(',')}&historyDays=504`,
            )
          : Promise.resolve(null),
      ]);
      setMc(mcResp.data);
      if (copulaResp) setCopula(copulaResp.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Scenario run failed.');
    } finally {
      setRunning(false);
    }
  };

  const updateFactor = (idx: number, patch: Partial<{ indexCode: string; beta: number }>) => {
    setActivePreset(-1);
    setFactors((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  const addFactor = () => {
    setActivePreset(-1);
    setFactors((prev) => [...prev, { indexCode: '', beta: 0 }]);
  };

  const removeFactor = (idx: number) => {
    setActivePreset(-1);
    setFactors((prev) => prev.filter((_, i) => i !== idx));
  };

  const applyPreset = (idx: number) => {
    setActivePreset(idx);
    setFactors(FACTOR_PRESETS[idx].factors.map((f) => ({ ...f })));
  };

  // Pull a parametric-VaR figure from the existing endpoint payload (lossely typed).
  const parametricDollar = useMemo(() => {
    if (!parametric?.var) return null;
    const v = parametric.var as any;
    // Try several known shapes from RiskAnalyticsService
    if (typeof v === 'number') return v;
    if (typeof v.oneDay99 === 'number') return v.oneDay99;
    if (typeof v.var99 === 'number') return v.var99;
    if (typeof v.var99Pct === 'number' && parametric.totalMarketValue)
      return (v.var99Pct / 100) * parametric.totalMarketValue;
    return null;
  }, [parametric]);

  const portfolioMv = mc?.marketValue ?? histStrip?.oneYear?.marketValue ?? parametric?.totalMarketValue;

  return (
    // #86: the per-portfolio VaR drilldown (/risk/portfolios/:id) is a standalone
    // route with no dark shell — the dark app body (222 84% 4.9%) shows through
    // once .portco-embedded makes the portco surfaces transparent, matching the
    // Cross-Asset landing and the rest of Analytics.
    <div className="portco-embedded">
      <PortcoGlobal />
      <PortcoEmbeddedDark />
      <Surface className="portco-surface">
        <Header>
          <HeaderInner>
            <HeaderBrand>
              <HeaderBrandMark>AF</HeaderBrandMark>
              <span>AxiomFlow / Risk · {portfolio?.name || `Portfolio ${pid}`}</span>
            </HeaderBrand>
            <HeaderCrumb>
              <span>Risk</span>
              <span> / </span>
              <span>Portfolio</span>
            </HeaderCrumb>
          </HeaderInner>
        </Header>

        <Body>
          <SmallLink onClick={() => navigate('/risk')}>← Back to Risk Center</SmallLink>

          <Hero>
            <HeroEyebrow>4-method VaR · factor-shock simulator · copula dependence</HeroEyebrow>
            <HeroTitle>{portfolio?.name || `Portfolio ${pid}`}</HeroTitle>
            <HeroSub>
              Cross-method VaR reconciliation. Parametric (duration × yield-vol) sits beside
              empirical historical VaR, factor-decomposed Monte Carlo (10k paths, Gaussian draws),
              and a copula correlation matrix that flags lower-tail co-exceedance between factors.
              Override the factor mix to stress-test sensitivities — every cell is sourced from
              fact_market_index_value, no fabricated values.
            </HeroSub>
          </Hero>

          {loading && <Loading>Loading risk metrics…</Loading>}
          {error && !loading && <Empty>{error}</Empty>}

          {!loading && (
            <>
              <StatStrip>
                <StatCell>
                  <StatLabel>Portfolio MV</StatLabel>
                  <StatValue>{fmtMoney(portfolioMv)}</StatValue>
                  <StatSub>As of {parametric?.asOfDate || 'today'}</StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Parametric 1d VaR</StatLabel>
                  <StatValue>{fmtMoney(parametricDollar)}</StatValue>
                  <StatSub>Duration × yield-vol × z @ 99%</StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Historical 1y VaR</StatLabel>
                  <StatValue>{fmtMoney(histStrip?.oneYear?.varDollars)}</StatValue>
                  <StatSub>
                    {histStrip?.oneYear?.observations ?? 0} obs · ES{' '}
                    {fmtMoney(histStrip?.oneYear?.expectedShortfallDollars)}
                  </StatSub>
                </StatCell>
                <StatCell>
                  <StatLabel>Monte Carlo VaR</StatLabel>
                  <StatValue>{fmtMoney(mc?.varDollars)}</StatValue>
                  <StatSub>
                    {fmtNum(mc?.paths, 0)} paths · ES {fmtMoney(mc?.expectedShortfallDollars)}
                  </StatSub>
                </StatCell>
              </StatStrip>

              <Panel>
                <PanelHead>
                  <PanelTitle>Method comparison @ {(confidence * 100).toFixed(0)}% confidence</PanelTitle>
                  <FactorPresetRow>
                    {CONFIDENCE_PRESETS.map((c) => (
                      <ChipBtn key={c} $active={confidence === c} onClick={() => setConfidence(c)}>
                        {(c * 100).toFixed(0)}%
                      </ChipBtn>
                    ))}
                  </FactorPresetRow>
                </PanelHead>
                <PanelBody>
                  <VarCmpGrid>
                    <VarCard $tone="parametric">
                      <VarLabel>Parametric</VarLabel>
                      <VarValue>{fmtMoney(parametricDollar)}</VarValue>
                      <VarSub>
                        Duration × yield-vol × z. Best for vanilla rates exposure; understates
                        tail risk in credit / equity / RE / PE.
                      </VarSub>
                    </VarCard>
                    <VarCard $tone="historical">
                      <VarLabel>Historical 1 / 3 / 5 yr</VarLabel>
                      <VarValue>
                        {fmtMoney(histStrip?.oneYear?.varDollars)}{' '}
                        <span style={{ color: portcoTokens.inkSubtle, fontSize: 13 }}>·</span>{' '}
                        {fmtMoney(histStrip?.threeYear?.varDollars)}{' '}
                        <span style={{ color: portcoTokens.inkSubtle, fontSize: 13 }}>·</span>{' '}
                        {fmtMoney(histStrip?.fiveYear?.varDollars)}
                      </VarValue>
                      <VarSub>
                        Empirical percentile of actual returns. Captures true tail shape but
                        bound by the historical sample.
                      </VarSub>
                    </VarCard>
                    <VarCard $tone="mc">
                      <VarLabel>Monte Carlo · {fmtNum(mc?.paths, 0)} paths</VarLabel>
                      <VarValue>{fmtMoney(mc?.varDollars)}</VarValue>
                      <VarSub>
                        Factor-decomposed Gaussian draws. ES{' '}
                        {fmtMoney(mc?.expectedShortfallDollars)}.
                        {mc?.factors?.length ? (
                          <>
                            <br />
                            Factors:{' '}
                            {mc.factors.map((f) => `${f.indexCode}×${f.beta.toFixed(2)}`).join(' + ')}
                          </>
                        ) : null}
                      </VarSub>
                    </VarCard>
                    <VarCard $tone="copula">
                      <VarLabel>Copula tail · {copula?.indices?.length || 0} factors</VarLabel>
                      <VarValue>
                        {copula?.observations ? `${copula.observations} obs` : '—'}
                      </VarValue>
                      <VarSub>
                        Spearman rank correlation + 5% lower-tail co-exceedance. Highlights
                        joint downside between factors.
                        {copula?.startDate && (
                          <>
                            <br />
                            Window: {copula.startDate} → {copula.endDate}
                          </>
                        )}
                      </VarSub>
                    </VarCard>
                  </VarCmpGrid>
                </PanelBody>
              </Panel>

              <Panel>
                <PanelHead>
                  <PanelTitle>Factor-shock simulator</PanelTitle>
                  <PanelMeta>POST /api/risk/var/montecarlo + copula matrix refresh</PanelMeta>
                </PanelHead>
                <PanelBody>
                  <TwoCol>
                    <div>
                      <FactorPresetRow>
                        {FACTOR_PRESETS.map((preset, i) => (
                          <ChipBtn
                            key={preset.label}
                            $active={activePreset === i}
                            onClick={() => applyPreset(i)}
                          >
                            {preset.label}
                          </ChipBtn>
                        ))}
                      </FactorPresetRow>

                      {factors.map((f, i) => (
                        <FactorRow key={i}>
                          <input
                            type="text"
                            value={f.indexCode}
                            placeholder="Index code (SPY, HYG, IWM, TLT…)"
                            onChange={(e) => updateFactor(i, { indexCode: e.target.value.toUpperCase() })}
                          />
                          <input
                            type="number"
                            step={0.05}
                            value={f.beta}
                            onChange={(e) => updateFactor(i, { beta: parseFloat(e.target.value) || 0 })}
                          />
                          <button
                            onClick={() => removeFactor(i)}
                            disabled={factors.length <= 1}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: portcoTokens.inkSubtle,
                              fontSize: 16,
                            }}
                            aria-label="Remove factor"
                            title="Remove"
                          >
                            ×
                          </button>
                        </FactorRow>
                      ))}
                      <ChipBtn onClick={addFactor}>+ Add factor</ChipBtn>
                      <RunButton onClick={runScenario} disabled={running || factors.length === 0}>
                        {running ? 'Running…' : 'Run scenario'}
                      </RunButton>
                    </div>

                    <div>
                      <VarLabel>Per-factor stats from history</VarLabel>
                      {mc?.factors?.length ? (
                        <Table>
                          <Thead>
                            <tr>
                              <Th>Index</Th>
                              <Th>β</Th>
                              <Th>μ daily</Th>
                              <Th>σ daily</Th>
                              <Th>σ annualized</Th>
                            </tr>
                          </Thead>
                          <tbody>
                            {mc.factors.map((f) => (
                              <Tr key={f.indexCode}>
                                <Td>{f.indexCode}</Td>
                                <TdMono>{f.beta.toFixed(2)}</TdMono>
                                <TdMono>{(f.muDaily * 10000).toFixed(1)} bp</TdMono>
                                <TdMono>{(f.sigmaDaily * 10000).toFixed(1)} bp</TdMono>
                                <TdMono>{(f.sigmaDaily * Math.sqrt(252) * 100).toFixed(2)}%</TdMono>
                              </Tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <Empty>Run a scenario to populate factor stats.</Empty>
                      )}
                    </div>
                  </TwoCol>
                </PanelBody>
              </Panel>

              {copula && copula.indices && copula.indices.length >= 2 && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Copula correlation · tail dependence</PanelTitle>
                    <PanelMeta>
                      {copula.observations} obs · {copula.startDate} → {copula.endDate}
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <VarLabel>Spearman rank (Gaussian copula proxy)</VarLabel>
                        <MatrixTable>
                          <thead>
                            <tr>
                              <th></th>
                              {copula.indices.map((c) => (
                                <th key={c}>{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {copula.indices.map((row) => (
                              <tr key={row}>
                                <td>{row}</td>
                                {copula.indices!.map((col) => {
                                  const v = copula.spearmanCopulaProxy?.[row]?.[col];
                                  return (
                                    <td key={col} style={{ background: corrCellBg(v as any) }}>
                                      {fmtNum(v as any, 3)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </MatrixTable>
                      </div>

                      <div>
                        <VarLabel>Lower-tail co-exceedance @ 5%</VarLabel>
                        <MatrixTable>
                          <thead>
                            <tr>
                              <th></th>
                              {copula.indices.map((c) => (
                                <th key={c}>{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {copula.indices.map((row) => (
                              <tr key={row}>
                                <td>{row}</td>
                                {copula.indices!.map((col) => {
                                  const v = copula.lowerTailCoExceedance5pct?.[row]?.[col];
                                  return (
                                    <td key={col} style={{ background: tailCellBg(v as any) }}>
                                      {fmtNum(v as any, 3)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </MatrixTable>
                      </div>
                    </div>
                  </PanelBody>
                </Panel>
              )}
            </>
          )}
        </Body>
      </Surface>
    </div>
  );
};

export default RiskDetailScreen;
